import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { useFormContext } from '@/components/react-hook-form';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { calculateResiduaryRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { isNonTaxableSource } from '@/modules/dispositiveProvisions/utils/taxUtils';
import { PathsOf } from '@/types/subform';
import { diagnostics } from '@/utils/diagnostics';

import { DispositiveProvisionsTemplateShape } from '../../DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.types';

/**
 * Returns the templateId for the given death section in the form.
 * @param deathEvent
 */
export function useTemplateId({
  deathEvent,
}: {
  deathEvent: PrimaryClientDeathEvent;
}) {
  const { control } = useFormContext<DispositiveProvisionsFormV2Shape>();

  return useWatch({
    control,
    name: `${getDeathEventBasePath(deathEvent)}.templateId` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>,
  });
}

/**
 * Returns the current recipients for the given death section in the form.
 * @param deathEvent
 */
export function useCurrentRecipients({
  deathEvent,
}: {
  deathEvent: PrimaryClientDeathEvent;
}) {
  const { control } = useFormContext<DispositiveProvisionsFormV2Shape>();

  const [recipients] = useWatch({
    control,
    name: [
      `${getDeathEventBasePath(deathEvent)}.recipients` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>,
    ],
  });

  return recipients;
}

/**
 * Returns whether the residuary pays all taxes for the given death section in the form.
 * @param deathEvent
 */
export function useResiduaryPaysAllTaxes({
  deathEvent,
}: {
  deathEvent: PrimaryClientDeathEvent | undefined | null;
}): {
  residuaryPaysAllTaxes: boolean;
  residuaryPaysAllTaxesFieldName: string;
} {
  const { control } = useFormContext<
    DispositiveProvisionsFormV2Shape | DispositiveProvisionsTemplateShape
  >();

  const residuaryPaysAllTaxesFieldName =
    `${getDeathEventBasePath(deathEvent)}.residuaryPaysAllTaxes` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

  const residuaryPaysAllTaxes = useWatch({
    control,
    name: residuaryPaysAllTaxesFieldName,
  });

  return { residuaryPaysAllTaxes, residuaryPaysAllTaxesFieldName };
}

/**
 * Watches the `residuaryPaysAllTaxes` field and updates the `bearsTax` field
 * for each recipient for the given death event.
 * @param deathEvent
 */
export function useToggleResiduaryPaysAllTaxes({
  deathEvent,
}: {
  deathEvent: PrimaryClientDeathEvent;
}) {
  const dispositiveProvisionsContext = useDispositiveProvisionsContext();
  const { setValue } = useFormContext<DispositiveProvisionsFormV2Shape>();

  // Use useWatch (via useCurrentRecipients) instead of useFieldArray.fields
  // because useFieldArray.fields does NOT reflect individual field value changes
  // made via form inputs. This caused calculateResiduaryRecipients to receive
  // stale disposition kinds/percentages and misidentify residuary recipients.
  const recipients = useCurrentRecipients({ deathEvent });

  const { residuaryPaysAllTaxes } = useResiduaryPaysAllTaxes({
    deathEvent,
  });

  const previousResiduaryPaysAllTaxes = usePrevious(residuaryPaysAllTaxes);

  useEffect(() => {
    if (
      previousResiduaryPaysAllTaxes === true &&
      !residuaryPaysAllTaxes &&
      recipients
    ) {
      diagnostics.debug('[useToggleResiduaryPaysAllTaxes] toggled OFF');

      // If toggled off, set each recipient's bearsTax field to TRUE unless the
      // source itself is non-taxable. The field toggle will be visible now,
      // allowing the user to override each recipient individually.
      recipients.forEach((_, index) => {
        // Update the nested `bearsTax` field directly so that the corresponding
        // Controller picks up the change and re-renders. Using `setValue` here
        // avoids the subtle issue where `useFieldArray.update` mutates the
        // array item but does not propagate the change to the nested field
        // subscription, causing the UI (SwitchInput) to appear out of sync.
        setValue(
          `${getDeathEventBasePath(deathEvent)}.recipients.${index}.bearsTax` as const,
          !isNonTaxableSource(dispositiveProvisionsContext),
          {
            shouldDirty: true,
          }
        );
      });

      return;
    }

    if (
      previousResiduaryPaysAllTaxes === false &&
      residuaryPaysAllTaxes &&
      recipients
    ) {
      diagnostics.debug('[useToggleResiduaryPaysAllTaxes] toggled ON');

      // If toggled on, identify the residuary recipients and set their bearsTax
      // field to TRUE. The rest should be set to FALSE. This will hide the
      // bearsTax toggle for each recipient.
      const { residuaryIndices } = calculateResiduaryRecipients(recipients);

      recipients.forEach((_, index) => {
        const isResiduary = residuaryIndices.includes(index);
        setValue(
          `${getDeathEventBasePath(deathEvent)}.recipients.${index}.bearsTax` as const,
          isResiduary,
          {
            shouldDirty: true,
          }
        );
      });
    }
  }, [
    residuaryPaysAllTaxes,
    previousResiduaryPaysAllTaxes,
    deathEvent,
    setValue,
    recipients,
    dispositiveProvisionsContext,
  ]);
}
